import { supabase } from '../config/supabase';
import { hasAnalyticsConsent } from '../config/consent';
import { isMobileMoneyProvider } from './busMoney';

// Conversion tracking: every event goes to GTM's dataLayer (when a
// container is configured) AND to the first-party analytics-ingest Edge
// Function, so funnels survive ad-blockers.
//
// Funnel events used across the app:
//   quote_requested   — bus: route+date+departure chosen; private: quote fetched
//   booking_created   — a booking row exists, checkout opened
//   payment_captured  — money received (first-party funnel)
//   purchase          — the same moment as a GA4/Google Ads conversion, via
//                       trackPurchase(): deduped per order, all payment paths

/**
 * May GTM-bound data (dataLayer, conversion markers) be used on this page?
 * Stored consent, or the loader legitimately ran: with the consent geo gate on,
 * visitors outside the consent regimes get GTM without a stored decision
 * (CookieBanner), and their conversions must still count. GTM can't be unloaded,
 * so withdrawing consent reloads the page and resets __rsGtmLoaded.
 */
function gtmAllowed() {
  return hasAnalyticsConsent() || window.__rsGtmLoaded === true;
}

const ingestUrl = () => `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/analytics-ingest`;
const SESSION_KEY = 'rs_analytics_session';

// Without consent we still count events, but they stop being linkable to each
// other: every event gets a throwaway id that is never written to the device.
function sessionId() {
  if (!hasAnalyticsConsent()) {
    // Covers routes that never touch the banner, such as a CONSENT_VERSION bump
    // invalidating an earlier acceptance.
    clearAnalyticsSession();
    return crypto.randomUUID();
  }

  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

// The only props kept when the visitor has not agreed to analytics.
//
// Deliberately a short allowlist rather than a denylist of known-bad keys: the
// next call site to add `email` or `pickup` would pass a denylist silently. All
// five are low-cardinality categories that describe the funnel step, not the
// person. Everything else -- orderNumber, bookingId, userId, pickup, dropoff,
// amount, price, currency, travelDate, seats -- identifies a booking or narrows
// to one, and is dropped.
//
// This is what makes the privacy notice's "carry no identifier" sentence true.
// Widening it means changing that sentence too.
const CONSENTLESS_PROPS = ['product', 'productType', 'route', 'isReturn', 'mode'];

/**
 * Drop the stored session id.
 *
 * Called when consent is withdrawn. Without it the id outlives the refusal, and
 * a later Accept resumes the same session -- stitching the new activity back to
 * everything recorded before the visitor said no.
 */
export function clearAnalyticsSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage unavailable; nothing was persisted to begin with.
  }
}

function redactProps(props) {
  const safe = {};
  for (const key of CONSENTLESS_PROPS) {
    if (props[key] !== undefined) safe[key] = props[key];
  }
  return safe;
}

function stripUserId(props) {
  const { userId: _userId, ...rest } = props;
  return rest;
}

async function ingestAuthHeader() {
  if (!hasAnalyticsConsent()) {
    return `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return `Bearer ${session.access_token}`;
    }
  } catch {
    // Fall through to anon key.
  }

  return `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`;
}

export function track(eventName, props = {}) {
  const consented = hasAnalyticsConsent();

  // GTM (no-op when no container configured).
  //
  // Gated on consent, not merely on whether GTM has loaded. dataLayer is a
  // queue: anything pushed before the container loads is replayed the moment it
  // does, so pushing pre-consent events would ship them to Google in full the
  // instant someone clicks Accept.
  try {
    if (gtmAllowed() && window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...stripUserId(props) });
    }
  } catch {
    // Analytics must never break the app.
  }

  // First-party (fire and forget)
  (async () => {
    try {
      const safeProps = consented ? stripUserId(props) : redactProps(props);
      const headers = {
        'Content-Type': 'application/json',
        Authorization: await ingestAuthHeader(),
      };

      await fetch(ingestUrl(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          events: [{
            event_name: String(eventName).slice(0, 64),
            session_id: sessionId(),
            props: safeProps,
          }],
        }),
      });
    } catch {
      // Never block on analytics.
    }
  })();
}

const PURCHASES_KEY = 'rs_purchases_reported';
const PURCHASES_KEPT = 50;

function alreadyReported(transactionId) {
  try {
    return JSON.parse(localStorage.getItem(PURCHASES_KEY) || '[]').includes(transactionId);
  } catch {
    return false;
  }
}

function markReported(transactionId) {
  try {
    const seen = JSON.parse(localStorage.getItem(PURCHASES_KEY) || '[]');
    localStorage.setItem(PURCHASES_KEY, JSON.stringify([...seen, transactionId].slice(-PURCHASES_KEPT)));
  } catch {
    // Storage unavailable: Google Ads still dedupes on transaction_id.
  }
}

/**
 * The paid-booking conversion for Google Ads and GA4: one GA4-standard
 * `purchase` event per order, from whichever path completed the payment
 * (in-page card, or a PayPal/Klarna/Alipay redirect back to /manage).
 *
 * - transaction_id is the order number, so a refreshed or revisited
 *   confirmation never counts twice (here, and again in Google Ads).
 * - value/currency are what was actually charged, never the display price.
 * - email/phone (when the path has them) are SHA-256 hashed here and sent as
 *   `user_data` (sha256_email_address / sha256_phone_number) for Ads enhanced
 *   conversions; the raw values never enter the dataLayer.
 *
 * Only ever reaches the dataLayer with consent, like every other event. The
 * first-party funnel keeps using `payment_captured`.
 */
async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Enhanced-conversions data, hashed here so raw contact details never enter
 * the dataLayer. Field names are the Google tag's pre-hashed keys. Normalised
 * per Google's spec first: trimmed lower-case email; phone as +digits.
 * Returns null when there is nothing to send or hashing is unavailable --
 * never the raw values.
 */
const HASH_RE = /^[0-9a-f]{64}$/;

/** Keeps only well-formed Google pre-hashed fields; anything else is dropped. */
function sanitizeUserData(userData) {
  if (!userData || typeof userData !== 'object') return null;
  const out = {};
  ['sha256_email_address', 'sha256_phone_number'].forEach((key) => {
    if (typeof userData[key] === 'string' && HASH_RE.test(userData[key])) out[key] = userData[key];
  });
  return Object.keys(out).length ? out : null;
}

async function hashedUserData(email, phone) {
  if (!window.crypto?.subtle) return null;
  const out = {};
  const normalEmail = email ? String(email).trim().toLowerCase() : '';
  const digits = phone ? String(phone).replace(/[^\d+]/g, '') : '';
  const normalPhone = digits.startsWith('+') ? digits : '';
  if (normalEmail) out.sha256_email_address = await sha256Hex(normalEmail);
  if (normalPhone) out.sha256_phone_number = await sha256Hex(normalPhone);
  return Object.keys(out).length ? out : null;
}

const purchasesInFlight = new Set();

export async function trackPurchase({
  transactionId, value, currency, product, route, email, phone, userData: preHashed,
} = {}) {
  const amount = Number(value);
  if (!transactionId || !Number.isFinite(amount) || amount <= 0 || !currency) return;
  if (!gtmAllowed() || !window.dataLayer) return;
  if (alreadyReported(transactionId) || purchasesInFlight.has(transactionId)) return;
  // In-memory guard for calls racing through the async hash. Nothing is marked
  // reported until the push has happened, so a tab closed mid-hash retries on
  // the next /manage visit.
  purchasesInFlight.add(transactionId);

  try {
    let userData = sanitizeUserData(preHashed);
    if (!userData) {
      try {
        userData = await hashedUserData(email, phone);
      } catch {
        userData = null; // send the conversion without enhanced data rather than not at all
      }
    }
    if (alreadyReported(transactionId)) return;

    const rounded = Math.round(amount * 100) / 100;
    // GA4 convention: clear the previous ecommerce object so fields can't leak
    // between events.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: String(transactionId),
        value: rounded,
        currency: String(currency).toUpperCase(),
        items: [
          {
            item_id: route || product || 'booking',
            item_name: route ? `${product} ${route}` : product || 'booking',
            item_category: product || 'booking',
            price: rounded,
            quantity: 1,
          },
        ],
      },
      ...(userData ? { user_data: userData } : {}),
    });
    markReported(transactionId);
    forgetPendingPurchase(transactionId);
  } catch {
    // Analytics must never break the app.
  } finally {
    purchasesInFlight.delete(transactionId);
  }
}

// Purchases completed away from the page that started them: PayPal / Klarna /
// Alipay and mobile money return to /manage, and a customer may reload, close
// the tab, or come back later via their email link. So the conversion is not
// tied to the return URL. The page that creates the booking leaves a marker in
// this browser; /manage reports it once the server says the payment is
// captured. A crafted /manage URL in another browser has no marker, and a hold
// (authorized) or a pending payment keeps waiting rather than counting.

const PENDING_KEY = 'rs_pending_purchases';
const PENDING_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const PENDING_KEPT = 20;
const CAPTURED_STATUSES = ['captured', 'paid'];
// manual_resolution is deliberately neither: it means uncertain money queued
// for ops review, so it waits until ops resolves it to captured or cancelled.
const DEAD_STATUSES = ['cancelled', 'cancelled_retained', 'refunded', 'abandoned', 'failed', 'expired'];

function readPending() {
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_KEY) || '{}');
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(all)
        .filter(([, entry]) => entry && now - entry.at < PENDING_TTL_MS)
        // Markers written by an early build held plain email/phone; never keep or use them.
        .map(([order, { email: _email, phone: _phone, ...rest }]) => [order, rest]),
    );
  } catch {
    return {};
  }
}

function writePending(pending) {
  try {
    const kept = Object.entries(pending)
      .sort(([, a], [, b]) => a.at - b.at)
      .slice(-PENDING_KEPT);
    localStorage.setItem(PENDING_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // Storage unavailable: only redirect-paid conversions are lost.
  }
}

function forgetPendingPurchase(orderNumber) {
  const pending = readPending();
  if (!pending[orderNumber]) return;
  delete pending[orderNumber];
  writePending(pending);
}

/**
 * Called where a booking is created. Only with consent: the marker exists to
 * feed the conversion, which needs consent anyway. Contact details are hashed
 * before storage, so the device never holds them in plain text for this.
 */
export async function rememberPendingPurchase({
  orderNumber, product, route, amount, currency, email, phone,
} = {}) {
  if (!orderNumber || !gtmAllowed()) return;
  let userData = null;
  try {
    userData = await hashedUserData(email, phone);
  } catch {
    userData = null;
  }
  const pending = readPending();
  // amount/currency: the booking total the server returned at creation. The
  // /manage summary is one leg of a return trip, and mobile money charges a
  // separate total, so the summary can't stand in for what was paid.
  pending[orderNumber] = { product, route, amount, currency, userData, at: Date.now() };
  writePending(pending);
}

/**
 * Called by /manage with the booking summary the server returned. Reports the
 * purchase once the payment is captured; clears the marker if the booking died.
 */
export function reportPendingPurchase(summary) {
  const orderNumber = summary?.orderNumber;
  if (!orderNumber) return;
  const entry = readPending()[orderNumber];
  if (!entry) return;

  if (alreadyReported(orderNumber)) {
    forgetPendingPurchase(orderNumber);
    return;
  }
  if (purchasesInFlight.has(orderNumber)) return;
  if (DEAD_STATUSES.includes(summary.paymentStatus)) {
    forgetPendingPurchase(orderNumber);
    return;
  }
  if (!CAPTURED_STATUSES.includes(summary.paymentStatus)) return;

  // Only keys the analytics-ingest allowlist accepts: an unknown key makes it
  // reject the whole event.
  // What was actually charged, most trusted first:
  // 1. mobile money: the server's DPO snapshot (full charge, both legs, KES/TZS),
  //    only when the booking was actually paid that way -- a DPO start that
  //    fails falls back to card but keeps the snapshot;
  // 2. the booking total the server returned at creation (covers return trips);
  // 3. the summary, for a marker with neither.
  const mobileMoney = isMobileMoneyProvider(summary.paymentProvider)
    && Number(summary.mobileMoneyAmount) > 0
    && summary.mobileMoneyCurrency;
  const hasTotal = Number(entry.amount) > 0 && entry.currency;
  let amount = summary.amount;
  let currency = summary.currency;
  if (mobileMoney) {
    amount = summary.mobileMoneyAmount;
    currency = summary.mobileMoneyCurrency;
  } else if (hasTotal) {
    amount = entry.amount;
    currency = entry.currency;
  }
  trackPaymentCaptured({
    product: entry.product,
    route: entry.route,
    orderNumber,
    amount,
    currency,
  });
  return trackPurchase({
    transactionId: orderNumber,
    value: amount,
    currency,
    product: entry.product,
    route: entry.route,
    userData: entry.userData,
  });
}

const CAPTURES_KEY = 'rs_captures_reported';

/**
 * The first-party `payment_captured` funnel event, at most once per order in
 * this browser: a private redirect is completed by the service and then seen
 * again by /manage. Dedupe state is only kept with consent; without it there
 * are no pending markers, so /manage never repeats a capture anyway.
 */
export function trackPaymentCaptured(props = {}) {
  const { orderNumber } = props;
  if (orderNumber && gtmAllowed()) {
    try {
      const seen = JSON.parse(localStorage.getItem(CAPTURES_KEY) || '[]');
      if (seen.includes(orderNumber)) return;
      localStorage.setItem(CAPTURES_KEY, JSON.stringify([...seen, orderNumber].slice(-PURCHASES_KEPT)));
    } catch {
      // Storage unavailable: fall through and record it.
    }
  }
  track('payment_captured', props);
}
